import type { ConvertibleNoteModel, LiqPrefLedgerConfig } from 'api';
import * as CAPTABLE from 'common/constants/cap-table';
import { YES } from 'common/constants/inputs';
import { CONVERTIBLE_NOTES, OPTION, PREFERRED_STOCK, WARRANT } from 'common/constants/securityTypes';
import { getSecurityType } from './underlyingSecurities';
import type { CapTableCell } from '../components/captable-table/CaptableTable.props';
import { PREFERRED_STOCK_VALUE } from '../components/convertible-notes-ledger/config/constants';

export interface FieldApplicabilityContext {
  has_dividend_rate_ledger?: boolean;
  has_liquidation_preference_ledger?: boolean;
  columns?: readonly {
    id?: number | string | null;
    security_ref?: string;
    dividend_rate_ledger_items?: unknown[];
  }[];
}

const columnCell = (cell: CapTableCell, cells: Record<string, CapTableCell>, row: number, alias: string) => {
  const keyed = cells[`${cell.columnLegend}${row}`];
  return keyed && (!keyed.alias || keyed.alias === alias)
    ? keyed
    : Object.values(cells).find(candidate => candidate.columnId === cell.columnId && candidate.alias === alias);
};

export const hasOwnPreferredTerms = (cell: CapTableCell, cells: Record<string, CapTableCell>): boolean => {
  const type = Number(columnCell(cell, cells, CAPTABLE.SECURITY_TYPE_ROW_NUMBER, CAPTABLE.SECURITY_TYPE_ALIAS)?.value);
  const notes = columnCell(cell, cells, CAPTABLE.CONVERTIBLE_NOTES_ROW_NUMBER, CAPTABLE.CONVERTIBLE_NOTES_ALIAS)?.value;
  return (
    type === PREFERRED_STOCK ||
    (type === CONVERTIBLE_NOTES &&
      Array.isArray(notes) &&
      notes.some((note: ConvertibleNoteModel) => !note.is_deleted && note.model_as_equity === PREFERRED_STOCK_VALUE))
  );
};

// Current grid values determine applicability; pending suggestions never enable another finding.
export const isCapTableFieldApplicable = (
  cell: CapTableCell,
  cells: Record<string, CapTableCell>,
  mode: 'value' | 'evidence' = 'value',
  context: FieldApplicabilityContext = {}
): boolean => {
  const valueAt = (row: number, alias: string) => columnCell(cell, cells, row, alias)?.value;
  const yes = (row: number, alias: string) => Number(valueAt(row, alias)) === YES;
  const type = Number(valueAt(CAPTABLE.SECURITY_TYPE_ROW_NUMBER, CAPTABLE.SECURITY_TYPE_ALIAS));
  const preferred = hasOwnPreferredTerms(cell, cells);
  const dividends = yes(CAPTABLE.CUMULATIVE_DIVIDENDS_ROW_NUMBER, CAPTABLE.CUMULATIVE_DIVIDENDS_ALIAS);
  let applicable: boolean;
  switch (cell.alias) {
    case CAPTABLE.CONVERSION_RATE_ALIAS:
    case CAPTABLE.LIQUIDATION_PREFERENCE_ALIAS:
    case CAPTABLE.SENIORITY_ALIAS: {
      const underlying = valueAt(CAPTABLE.UNDERLYING_SECURITY_ROW_NUMBER, CAPTABLE.UNDERLYING_SECURITY_ALIAS);
      const inherited =
        [OPTION, WARRANT].includes(type) && Number(getSecurityType(String(underlying), cells)) === PREFERRED_STOCK;
      applicable =
        (preferred &&
          (cell.alias !== CAPTABLE.CONVERSION_RATE_ALIAS || type === PREFERRED_STOCK || mode === 'evidence')) ||
        (mode === 'evidence' && inherited);
      break;
    }
    case CAPTABLE.PARTICIPATION_ALIAS:
    case CAPTABLE.CUMULATIVE_DIVIDENDS_ALIAS:
      applicable = preferred;
      break;
    case CAPTABLE.IS_PARTICIPATION_CAP_ALIAS:
    case CAPTABLE.PARTICIPATION_CAP_ALIAS:
      applicable =
        preferred &&
        yes(CAPTABLE.PARTICIPATION_ROW_NUMBER, CAPTABLE.PARTICIPATION_ALIAS) &&
        (cell.alias !== CAPTABLE.PARTICIPATION_CAP_ALIAS ||
          yes(CAPTABLE.IS_PARTICIPATION_CAP_ROW_NUMBER, CAPTABLE.IS_PARTICIPATION_CAP_ALIAS));
      break;
    case CAPTABLE.DIVIDEND_RATE_ALIAS:
    case CAPTABLE.DIVIDEND_PAYMENT_TYPE_ALIAS:
    case CAPTABLE.COMPOUNDING_ALIAS:
    case CAPTABLE.COMPOUNDING_PERIOD_ALIAS:
      applicable =
        preferred &&
        dividends &&
        (cell.alias !== CAPTABLE.COMPOUNDING_PERIOD_ALIAS ||
          yes(CAPTABLE.COMPOUNDING_ROW_NUMBER, CAPTABLE.COMPOUNDING_ALIAS));
      break;
    case CAPTABLE.INVESTMENT_DATE_ALIAS:
      return ![OPTION, WARRANT].includes(type);
    default:
      return true;
  }
  if (!applicable || mode === 'evidence') {
    return applicable;
  }
  const column = context.columns?.find(
    candidate =>
      (cell.columnRef != null && candidate.security_ref === cell.columnRef) ||
      (cell.columnId != null && candidate.id != null && Number(candidate.id) === Number(cell.columnId))
  );
  const dividendLedger = valueAt(CAPTABLE.DIVIDEND_RATE_LEDGER_ROW_NUMBER, CAPTABLE.DIVIDEND_RATE_LEDGER_ALIAS);
  const hasDividendLedger = Boolean(
    column?.dividend_rate_ledger_items?.length || (Array.isArray(dividendLedger) && dividendLedger.length)
  );
  if (cell.alias === CAPTABLE.COMPOUNDING_PERIOD_ALIAS && hasDividendLedger) {
    return false;
  }
  if (type !== PREFERRED_STOCK) {
    return true;
  }
  const multipleInvestments = yes(
    CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER,
    CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ALIAS
  );
  const liquidationLedger = valueAt(
    CAPTABLE.LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER,
    CAPTABLE.LIQUIDATION_PREFERENCE_LEDGER_ALIAS
  ) as LiqPrefLedgerConfig | undefined;
  const liquidationOwned =
    context.has_liquidation_preference_ledger &&
    !multipleInvestments &&
    liquidationLedger?.threshold_type &&
    CAPTABLE.liqPrefLedgerInfluencedCellAliases.some(({ alias }) => alias === cell.alias);
  const dividendOwned =
    context.has_dividend_rate_ledger &&
    !multipleInvestments &&
    hasDividendLedger &&
    CAPTABLE.divRateLedgerInfluencedCellAliases.some(({ alias }) => alias === cell.alias);
  return !liquidationOwned && !dividendOwned;
};
